import { useEffect, useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

/**
 * InvoicesLayout
 * --------------
 * Wraps the three Invoices tabs (Invoice Tracker / New Invoices /
 * Sent Invoices) under a single `/invoices` route hierarchy. The
 * previous setup put both "find suppliers to bill" and "look at
 * already-generated invoices" on one long page; the new layout adds
 * an Invoice Tracker tab dedicated to payment-status follow-up.
 *
 * Routing layout (see App.jsx):
 *   /invoices              → redirects to /invoices/tracker
 *   /invoices/tracker      → InvoiceTracker (filterable list with
 *                            inline status dropdown for payment
 *                            tracking — the default landing tab)
 *   /invoices/new          → NewInvoices (suppliers with unbilled
 *                            approved Billing Tracker entries)
 *   /invoices/sent         → SentInvoices (existing detailed list
 *                            view of generated invoice records)
 *
 * Deep links like /invoices/draft/<supplierCode> (Submit Invoice
 * draft page) keep working — they sit outside this layout because
 * they're a separate full-screen workflow.
 */
export default function InvoicesLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Bare /invoices bounces to the Invoice Tracker (default landing
  // tab). Replace history so the back button doesn't trap the user.
  useEffect(() => {
    if (
      location.pathname === '/invoices' ||
      location.pathname === '/invoices/'
    ) {
      navigate('/invoices/tracker', { replace: true });
    }
  }, [location.pathname, navigate]);

  const tabs = useMemo(
    () => [
      { to: '/invoices/tracker', label: 'Invoice Manager' },
      { to: '/invoices/new', label: 'New Invoices' },
      { to: '/invoices/sent', label: 'Sent Invoices' },
      { to: '/invoices/pos', label: 'PO Tracker' },
      { to: '/invoices/statements', label: 'Account Statements' },
    ],
    [],
  );

  return (
    <>
      {/* Tab strip — sits above each child page's page-header so the
          child pages can keep their own context-aware h1 and refresh
          button. Active tab gets the orange underline + brand-ink
          label; inactive tabs sit in muted-gray. */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--brand-border, #e5e7eb)',
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={false}
            style={({ isActive }) => ({
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 500,
              color: isActive ? 'var(--brand-ink)' : 'var(--brand-muted)',
              borderBottom: isActive
                ? '2px solid var(--brand-primary)'
                : '2px solid transparent',
              textDecoration: 'none',
              marginBottom: -1,
            })}
          >
            {t.label}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </>
  );
}
